import {useState, useEffect} from 'react';
import {Dimensions, Platform} from 'react-native';
import {RFValue} from 'react-native-responsive-fontsize';
import {getByScreenHeight, getByScreenSize, isWeb} from '../utils/responsive';

/**
 * Static bucket-based font scaling for web.
 * Uses width, height, and aspect-ratio thresholds to choose a fixed multiplier.
 * Native (iOS/Android) keeps RFValue behavior.
 */
const getSmartFontSize = (baseSize: number, width: number, height: number): number => {
  if (!isWeb) {
    // Native: keep RFValue but apply small reduction by device width, then global -20%
    const referenceHeight = height * getByScreenSize(Platform.OS === 'android' ? 0.95 : 1, 1.2);
    const rfSize = RFValue(baseSize, referenceHeight);
    const isPhone = width < 480;
    const isTablet = width >= 480 && width < 1024;
    const nativeScale = isPhone ? 0.95 : isTablet ? 0.93 : 0.95;
    const extraMobileTablet = (isPhone || isTablet) ? 0.9 : 1; // additional -10% for phone/tablet
    const extraLargeNative = (!isPhone && !isTablet) ? 0.95 : 1; // additional -5% for large native devices
    const reduced = rfSize * nativeScale * 0.8 * extraMobileTablet * extraLargeNative; // apply all factors
    return Math.max(8, Math.min(60, reduced));
  }

  const aspectRatio = width / Math.max(1, height);

  // We'll compute a size and apply a global -20% at the end
  let chosenSize: number | undefined;
  // Specific overrides for reported sizes
  // 429×901 (≈0.476) and close
  if (width >= 420 && width <= 440 && height >= 880 && height <= 920) {
    chosenSize = baseSize * 0.85;
  }
  // 538×742 (≈0.725) and close
  if (!chosenSize && width >= 520 && width <= 560 && height >= 720 && height <= 760) {
    chosenSize = baseSize * 0.85;
  }
  // 841×418 (≈2.01) and close
  if (!chosenSize && width >= 820 && width <= 860 && height >= 400 && height <= 440) {
    chosenSize = baseSize * 0.8;
  }

  // General buckets
  const isMobileWidth = width < 480;
  const isTabletWidth = width >= 480 && width < 1024;
  const isDesktopWidth = width >= 1024;

  let scale = 1;

  if (isMobileWidth) {
    // Mobile
    if (aspectRatio < 0.6) scale = 0.85; // tall & narrow
    else if (aspectRatio > 1.8) scale = 0.85; // wide & short
    else scale = 0.85; // normal
  } else if (isTabletWidth) {
    // Tablet / small laptop
    if (aspectRatio < 0.6) scale = 0.8;
    else if (aspectRatio > 2.0) scale = 0.85;
    else scale = 1.0;
  } else if (isDesktopWidth) {
    // Desktop and larger
    if (aspectRatio < 0.6) scale = 0.98;
    else if (aspectRatio > 2.2) scale = 0.88; // ultra wide
    else if (aspectRatio > 1.8) scale = 0.85; // wide
    else scale = 1.05; // normal desktop
  }

  const sizeBeforeGlobal = chosenSize !== undefined ? chosenSize : baseSize * scale;
  const extraMobileTabletWeb = width < 1024 ? 0.9 : 1; // additional -10% for mobile/tablet widths on web
  const extraLargeWeb = width >= 1024 ? 0.95 : 1; // additional -5% for large web devices
  const finalSize = sizeBeforeGlobal * 0.8 * extraMobileTabletWeb * extraLargeWeb; // apply all factors
  return Math.max(8, Math.min(60, finalSize));
};

/**
 * Hook that provides responsive font sizes that update when the window is resized on web
 * @returns Object containing font size calculation function and current screen dimensions
 */
export const useResponsiveFontSize = () => {
  const [dimensions, setDimensions] = useState(() => {
    if (isWeb && typeof window !== 'undefined') {
      // For web, use window dimensions directly for more accurate initial values
      return {
        width: window.innerWidth,
        height: window.innerHeight,
      };
    }
    const {height, width} = Dimensions.get('screen');
    return {height, width};
  });

  useEffect(() => {
    if (!isWeb) {
      // For native platforms, use the existing static approach
      return;
    }

    const updateDimensions = () => {
      if (typeof window !== 'undefined') {
        // For web, use window dimensions directly
        setDimensions({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      } else {
        // Fallback to React Native Dimensions
        const {height, width} = Dimensions.get('screen');
        setDimensions({height, width});
      }
    };

    // Listen for window resize events on web
    if (typeof window !== 'undefined') {
      // Force an initial update to ensure correct dimensions on page load
      updateDimensions();
      
      // Also update after a short delay to catch any late dimension changes
      const timeoutId = setTimeout(updateDimensions, 100);
      
      window.addEventListener('resize', updateDimensions);
      
      // Also listen for orientation change events
      window.addEventListener('orientationchange', updateDimensions);
      
      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', updateDimensions);
        window.removeEventListener('orientationchange', updateDimensions);
      };
    }
  }, []);

  // Calculate the reference height based on current dimensions (for native compatibility)
  const getScreenHeightReference = () => {
    return dimensions.height *
      (isWeb
        ? getByScreenHeight(2.1, getByScreenSize(1, 1.4))
        : getByScreenSize(Platform.OS === 'android' ? 0.95 : 1, 1.2));
  };

  // Function to get responsive font size using smart scaling
  const getFontSize = (size: number) => {
    return getSmartFontSize(size, dimensions.width, dimensions.height);
  };

  return {
    getFontSize,
    dimensions,
    screenHeightReference: getScreenHeightReference(),
    aspectRatio: dimensions.width / dimensions.height,
  };
};
